import { api } from '@/api'
import { resolveProductCategoryByPath } from '@/router/pageConst'
import { makeAutoObservable } from 'mobx'

interface Corp {
  freeTrial: boolean
  hasApply: boolean
  brandNormStatus: boolean
  freeTrialStatusMap: Partial<Record<ProjectModels.ProjectCategory, FreeTrialStatus>>
}

interface FreeTrialStatus {
  freeTrial: boolean
  hasApply: boolean
  loaded: boolean
}

type FreeTrialStatusResponse = Omit<FreeTrialStatus, 'loaded'>

interface RefreshFreeTrialStatusOptions {
  force?: boolean
}

function getCurrentProjectCategory(): ProjectModels.ProjectCategory {
  return resolveProductCategoryByPath(window.location.pathname)
}

export class CorpService {
  public corpInfo: Corp | null = null
  private freeTrialStatusRequestMap = new Map<ProjectModels.ProjectCategory, Promise<FreeTrialStatusResponse>>()

  constructor() {
    makeAutoObservable(this)
  }

  private async requestFreeTrialStatus(
    projectCategory: ProjectModels.ProjectCategory,
    options: RefreshFreeTrialStatusOptions = {}
  ) {
    const pendingRequest = this.freeTrialStatusRequestMap.get(projectCategory)
    if (pendingRequest && !options.force) {
      return pendingRequest
    }
    const request = api.corp.getCorpFreeTrialStatus({ projectCategory }).send(true)
    if (!options.force) {
      this.freeTrialStatusRequestMap.set(projectCategory, request)
    }
    try {
      return await request
    } finally {
      if (!options.force) {
        this.freeTrialStatusRequestMap.delete(projectCategory)
      }
    }
  }

  public async getCorpInfo(projectCategory: ProjectModels.ProjectCategory = getCurrentProjectCategory()) {
    const [{ freeTrial, hasApply }, { brandNormStatus }] = await Promise.all([
      this.requestFreeTrialStatus(projectCategory),
      api.corp.getCorpBrandNormStatus().send(true)
    ])
    const nextStatus = { freeTrial, hasApply, loaded: true }
    this.corpInfo = {
      brandNormStatus,
      freeTrial,
      hasApply,
      freeTrialStatusMap: {
        ...this.corpInfo?.freeTrialStatusMap,
        [projectCategory]: nextStatus,
      },
    }
    return this.corpInfo
  }

  public async refreshFreeTrialStatus(
    projectCategory: ProjectModels.ProjectCategory = getCurrentProjectCategory(),
    options: RefreshFreeTrialStatusOptions = {}
  ) {
    const cachedStatus = this.corpInfo?.freeTrialStatusMap[projectCategory]
    if (cachedStatus && !options.force) {
      return cachedStatus
    }
    const { freeTrial, hasApply } = await this.requestFreeTrialStatus(projectCategory, options)
    const nextStatus = { freeTrial, hasApply, loaded: true }
    this.corpInfo = {
      brandNormStatus: this.corpInfo?.brandNormStatus ?? false,
      freeTrial,
      hasApply,
      freeTrialStatusMap: {
        ...this.corpInfo?.freeTrialStatusMap,
        [projectCategory]: nextStatus,
      },
    }
  }

  public getFreeTrialStatus(projectCategory: ProjectModels.ProjectCategory): FreeTrialStatus {
    return this.corpInfo?.freeTrialStatusMap[projectCategory] || {
      freeTrial: false,
      hasApply: false,
      loaded: false,
    }
  }

}


export const corpService = new CorpService()
